/**
 * L0 incident feed (dashboard design rules §7): the three newest things that
 * went wrong across the estate — active M365 incidents/advisories merged with
 * firing Azure Monitor alerts — as a `Timeline`. Each entry links into its L1
 * board; the full lists live there, never here.
 *
 * Honesty (§5): the two feeds fail independently. A missing feed is named in
 * a footnote instead of silently shrinking the merged view, and severity is
 * never colour-alone — every marker pairs an icon with a text label.
 */

import { CircleAlert, Icon, type IconComponent, Info } from "@norican/icons";
import { Card, EmptyState, Freshness, Skeleton, Timeline, Typography } from "@norican/react";
import { Link } from "react-router";

import type { CloudAlerts, M365ServiceHealth } from "@/lib/api";
import { formatRelativeTime, severityStatus } from "@/lib/charts/config";

import type { FeedState } from "./feed-state";

/** How many merged events the L0 shows — the boards hold the rest. */
const FEED_SIZE = 3;

type FeedTone = "danger" | "warning" | "success" | "accent" | "default";

interface FeedEvent {
  id: string;
  /** ISO timestamp used for the newest-first merge; null sorts last. */
  at: string | null;
  tone: FeedTone;
  icon: IconComponent;
  title: string;
  /** The L1 board the entry drills into. */
  to: string;
  detail: string;
}

function m365Events(health: M365ServiceHealth): FeedEvent[] {
  if (!health.configured || health.status_reason !== "ok") {
    return [];
  }
  return health.active_incidents.map((incident) => ({
    id: `m365-${incident.id}`,
    at: incident.start_date_time ?? incident.last_modified_date_time,
    tone: incident.classification === "Incident" ? ("danger" as const) : ("warning" as const),
    icon: incident.classification === "Incident" ? CircleAlert : Info,
    title: incident.title,
    to: "/m365",
    detail: `M365 · ${incident.service} · ${incident.classification}`,
  }));
}

function cloudEvents(alerts: CloudAlerts): FeedEvent[] {
  if (!alerts.configured) {
    return [];
  }
  return alerts.alerts
    .filter((alert) => alert.monitor_condition === "Fired")
    .map((alert) => {
      const severity = severityStatus(alert.severity);
      return {
        id: `cloud-${alert.id}`,
        at: alert.fired_at,
        // The Timeline has no "neutral" tone; informational severities map to
        // its plain default marker.
        tone: severity.tone === "neutral" ? ("default" as const) : severity.tone,
        icon: severity.icon,
        title: alert.name,
        to: "/cloud",
        detail: `Azure · ${severity.label} · ${alert.subscription_name}`,
      };
    });
}

/** Newest first; events without a timestamp sort last. */
function newestFirst(a: FeedEvent, b: FeedEvent): number {
  if (a.at == null) {
    return 1;
  }
  if (b.at == null) {
    return -1;
  }
  return new Date(b.at).getTime() - new Date(a.at).getTime();
}

interface IncidentFeedProps {
  alerts: FeedState<CloudAlerts>;
  m365: FeedState<M365ServiceHealth>;
}

export function IncidentFeed({ alerts, m365 }: IncidentFeedProps) {
  const bothPending = alerts.data === undefined && m365.data === undefined;

  const events = [
    ...(alerts.data ? cloudEvents(alerts.data) : []),
    ...(m365.data ? m365Events(m365.data) : []),
  ]
    .sort(newestFirst)
    .slice(0, FEED_SIZE);

  // Name every feed that could not contribute, instead of silently shrinking.
  const gaps: string[] = [];
  if (alerts.isError) {
    gaps.push("Azure alerts feed unreachable");
  } else if (alerts.data && !alerts.data.configured) {
    gaps.push("Azure alerts not configured");
  }
  if (m365.isError) {
    gaps.push("M365 feed unreachable");
  } else if (m365.data && (!m365.data.configured || m365.data.status_reason !== "ok")) {
    gaps.push("M365 incidents unavailable");
  }

  const readTimes = [alerts.data?.collected_at, m365.data?.last_updated_at]
    .filter((value): value is string => value != null)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  const oldestRead = readTimes[0];

  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography variant="heading-4" as="h2">
            Newest incidents
          </Typography>
          {oldestRead && <Freshness label="Feeds read" since={oldestRead} />}
        </div>
      </Card.Header>
      <Card.Body>
        {bothPending && !alerts.isError && !m365.isError ? (
          <Skeleton height="10rem" />
        ) : events.length === 0 ? (
          <EmptyState
            size="sm"
            title="Nothing active right now"
            description="No open M365 incidents and no firing Azure alerts."
          />
        ) : (
          <Timeline
            orientation="vertical"
            items={events.map((event) => ({
              id: event.id,
              tone: event.tone,
              icon: <Icon icon={event.icon} size="sm" />,
              timestamp: formatRelativeTime(event.at),
              title: (
                <Link to={event.to} className="text-fg font-medium hover:underline">
                  {event.title}
                </Link>
              ),
              description: (
                <Typography variant="caption" as="span">
                  {event.detail}
                </Typography>
              ),
            }))}
          />
        )}
        {gaps.length > 0 && (
          <Typography variant="caption" as="p" className="mt-3">
            Partial view: {gaps.join(" · ")}.
          </Typography>
        )}
      </Card.Body>
    </Card>
  );
}
